import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircularProgress, TextField, Typography, Chip } from "@mui/material";

import { deleteProgramme, programmeListing } from "src/DAL/Programme/Programme";
import { s3baseUrl } from "src/config/config";
import { convertToTitleCase, htmlDecode } from "src/utils/convertHtml";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import { filter } from "lodash";
import { useNavigate } from "react-router-dom";
import CustomPopoverSection from "src/components/MenuOption/CustomPopoverSection";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}
function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) => _user.title.toLowerCase().indexOf(query.toLowerCase()) !== -1,
    );
  }
  return stabilizedThis.map((el) => el[0]);
}
export default function ProgrammesTemplate() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [orderBy, setOrderBy] = useState("number");
  const [order, setOrder] = useState("asc");
  const [filterName, setFilterName] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState("");

  const {
    data: program = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["programmes-template"],
    queryFn: async () => {
      const programType = true;
      const result = await programmeListing(programType);
      if (result.code === 200) {
        return result.program;
      } else {
        throw new Error(result.message);
      }
    },
  });

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  }, [error]);

  const handleNavigateDetail = (value) => {
    navigate(
      `/programme_templats/programme_templats-detail/${value.program_slug}`,
      {
        state: value,
      },
    );
  };

  const filteredPrograme = applySortFilter(
    program,
    getComparator(order, orderBy),
    filterName.trim(),
  );

  const handleNavigateEdit = (value) => {
    navigate(
      `/programme_templats/edit-programme_templats/${value?.program_slug}`,
      {
        state: value,
      },
    );
  };

  const handleNavigateCalendar = (value) => {
    if (value.program_length_type == "limited") {
      navigate(`/programme_templats-limited-calendar/${value?._id}/${value?.program_slug}`);
    } else {
      navigate(`/programme_templats-calendar/${value?._id}/${value?.program_slug}`);
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async (programSlug) => {
      const result = await deleteProgramme(programSlug);
      if (result.code !== 200) {
        throw new Error(result.message);
      }
      return result;
    },
    onSuccess: (result) => {
      handleCloseDelete();
      enqueueSnackbar(result.message, { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["programmes-template"] });
    },
    onError: (mutationError) => {
      enqueueSnackbar(mutationError.message, { variant: "error" });
      handleCloseDelete();
    },
  });

  const handleDelete = async () => {
    if (!deleteDoc?.program_slug) return;
    deleteMutation.mutate(deleteDoc.program_slug);
  };
  const handleOpenDelete = (value) => {
    setDeleteDoc(value);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => setOpenDelete(false);

  const MENU_OPTIONS = [
    {
      label: "Edit",
      icon: "akar-icons:edit",
      handleClick: handleNavigateEdit,
    },

    {
      label: "Details",
      icon: "bx:detail",
      handleClick: handleNavigateDetail,
    },

    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: handleOpenDelete,
    },
  ];

  if (isLoading === true) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  const isRecordNotFound = filteredPrograme.length === 0;
  return (
    <div className="container mt-2">
      <CustomConfirmation
        handleOpenDelete={handleOpenDelete}
        setOpen={setOpenDelete}
        handleCloseDelete={handleCloseDelete}
        handleAgree={handleDelete}
        open={openDelete}
        setOpenDelete={setOpenDelete}
      />
      <div className="row">
        <div className="col-lg-6 col-sm-12">
          <h2>Coach Programme Template</h2>
        </div>
        <div className="col-lg-6 col-sm-12 d-flex justify-content-end">
          <TextField
            fullWidth
            label="Search"
            sx={{ marginRight: 2, width: 240 }}
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
          />
          <button
            onClick={() =>
              navigate(`/programme_templats/add-programme_templats`)
            }
            className="small-contained-button"
          >
            Add Template
          </button>
        </div>
        {filteredPrograme.map((value, index) => {
          return (
            <div className="col-lg-4 col-md-4 col-sm-12 mb-4" key={index}>
              <div className="card mt-4 pods-cards-shadow cursor h-100 overflow-hidden ">
                {!value?.status && (
                  <span
                    style={{
                      position: "absolute",
                      top: "36px",
                      justifyContent: "center",
                      zIndex: "1",
                      left: "-38px",
                      transform: "rotate(-53deg)",
                    }}
                  >
                    <Chip
                      label={"Inactive"}
                      variant="contained"
                      color={"error"}
                      sx={{
                        fontSize: "10px",
                        height: "auto",
                        width: "150px",
                      }}
                    />
                  </span>
                )}
                <img
                  src={s3baseUrl + value.program_images.thumbnail_1}
                  className="card-img-top pods-image"
                  alt="Programme"
                  onClick={() => handleNavigateCalendar(value)}
                />
                <div className="card-body">
                  <div className="row">
                    <div className="col-12">
                      <h3
                        className="h2-heading"
                        onClick={() => handleNavigateCalendar(value)}
                      >
                        {htmlDecode(value.title)}
                      </h3>
                    </div>

                    <div className="col-2 programmes-menu-option">
                      <CustomPopoverSection menu={MENU_OPTIONS} data={value} />
                    </div>
                  </div>
                  <p
                    className="programme-card-desc mb-3"
                    onClick={() => handleNavigateCalendar(value)}
                  >
                    {htmlDecode(value.short_description)}
                  </p>
                  <div
                    className="row recording-card-date mb-2"
                    onClick={() => handleNavigateCalendar(value)}
                  >
                    <div className="col-5 card-button recording-card-date-position">
                      <p className="pods-active-members"></p>
                    </div>
                    <div className="col-7 text-end ms-auto pods-active-members text-capital">
                      {`${
                        value.program_type ? value.program_type : "General"
                      } | ${convertToTitleCase(value.program_length_type)}`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {isRecordNotFound && (
          <Typography
            variant="h5"
            align="center"
            style={{ marginTop: 90 }}
            justifyContent="center"
          >
            No results found
          </Typography>
        )}
      </div>
    </div>
  );
}
